import type { AgentTemplate } from "../types";
import type { AgentBody } from "@/schemas/agent.schema";
import { ref } from "vue";
import { useRouter } from "vue-router";
import { closeModal } from "@/components/providers/DialogProvider";
import { toaster } from "@/components/providers/ToasterProvider";
import {
  FALLBACK_LLM_MODEL,
  FALLBACK_TTS_MODEL,
  useDefaultModels,
} from "@/composables/useDefaultModels";
import { $t, locale } from "@/services/i18n";
import { createDefaultCallConfiguration } from "@/utils/voice-agent-defaults";
import { useDependencyResolver } from "./useDependencyResolver";

/**
 * Hook para aplicar templates e navegar para criação de agente
 */
export function useApplyTemplate() {
  const router = useRouter();
  const { resolveDependencies, state: dependencyState, reset } = useDependencyResolver();
  const { defaultLlmModel, defaultTtsModel } = useDefaultModels();

  const isApplying = ref(false);

  /**
   * Aplica um template e navega para criação de agente
   * @param template Template selecionado ou null para "Começar do Zero"
   */
  async function applyTemplate(template: AgentTemplate | null) {
    if (isApplying.value) return;

    isApplying.value = true;
    reset();

    try {
      let agentData: Partial<AgentBody>;
      let postAnalysisIds: string[] = [];

      // Modelos resolvidos dinamicamente do catálogo (com fallback), substituindo
      // os antigos hardcodes; templates não trazem mais llm_model/tts_model.
      const llmModel = defaultLlmModel.value ?? FALLBACK_LLM_MODEL;
      const ttsModel = defaultTtsModel.value ?? FALLBACK_TTS_MODEL;

      if (template) {
        // Resolver dependências de StructuredAnalysis se existirem
        if (template.analysisDependencies?.length) {
          const result = await resolveDependencies(template.analysisDependencies);
          postAnalysisIds = result.createdIds;

          if (result.errors.length > 0) {
            toaster.warning($t("agents:templates.dependency_warning"));
          }
        }

        // Construir dados do agente a partir do template
        const templateLanguage = template.config.language || locale.value;
        agentData = {
          name: template.config.name || "",
          description: template.config.description || "",
          instructions: template.config.instructions || "",
          language: templateLanguage,
          llm_model: template.config.llm_model || llmModel,
          tts_model: template.config.tts_model || ttsModel,
          tts_voice: template.config.tts_voice || "",
          tools: template.config.tools || [],
          parameters: template.config.parameters || {},
          call_configuration: {
            ...createDefaultCallConfiguration(templateLanguage),
            ...template.config.call_configuration,
          },
          post_analysis: postAnalysisIds,
          mcp_servers: [],
          share: "organization",
        };
      } else {
        // Template "Começar do Zero" - valores padrão
        agentData = {
          name: "",
          description: "",
          instructions: "",
          language: locale.value,
          llm_model: llmModel,
          tts_model: ttsModel,
          tts_voice: "",
          tools: [],
          parameters: {},
          call_configuration: createDefaultCallConfiguration(locale.value),
          post_analysis: [],
          mcp_servers: [],
          share: "organization",
        };
      }

      // Fechar modal antes de navegar
      closeModal();

      // Navegar para CreateAgent com dados pré-preenchidos
      // Using JSON.parse/stringify to serialize complex objects for history state
      await router.push({
        name: "Create Agent",
        params: { tab: "profile" },
        state: { agent: JSON.parse(JSON.stringify(agentData)) },
      });
    } catch (error) {
      console.error("Error applying template:", error);
      toaster.error($t("agents:templates.apply_error"));
    } finally {
      isApplying.value = false;
    }
  }

  return {
    applyTemplate,
    isApplying,
    dependencyState,
  };
}
